/**
 * app/screens/EditRoomScreen.tsx
 * -------------------------------
 * What: Form for editing an existing room.
 * Why: Supports maintaining a tidy room taxonomy for items.
 */
import React, { useState, useEffect } from 'react';
import {
  View,
  Text,
  TextInput as BaseTextInput,
  TouchableOpacity,
  StyleSheet,
  Alert,
  KeyboardAvoidingView,
  Platform,
  ScrollView,
  Animated,
  SafeAreaView,
  StatusBar,
  useWindowDimensions,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { useNavigation, useRoute, RouteProp, CommonActions } from '@react-navigation/native';
import type { NativeStackNavigationProp } from '@react-navigation/native-stack';
import type { RootStackParamList } from '../types/navigation';
import { supabase } from '../../lib/supabase';
import { usePaginatedRooms } from '../../lib/DataContext';
import { useAuth } from '../../lib/AuthContext';
import { useTheme } from '../../lib/ThemeContext';
import { getPlatform } from '../utils/platform';
import FormTextInput from '../components/FormTextInput';
import FormLabel from '../components/FormLabel';
import ThemedButton from '../components/ThemedButton';

type NavigationProp = NativeStackNavigationProp<RootStackParamList>;
type EditRoomRouteProp = RouteProp<RootStackParamList, 'EditRoom'>;

export default function EditRoomScreen() {
  const navigation = useNavigation<NavigationProp>();
  const route = useRoute<EditRoomRouteProp>();
  const { roomId } = route.params;
  const { width } = useWindowDimensions();
  const isSmallScreen = width < 768;
  const { user } = useAuth();
  const { refresh } = usePaginatedRooms();
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { theme, themeType } = useTheme();

  const cardGlow = themeType === 'blueNeon'
    ? (Platform.OS === 'web'
        ? { boxShadow: `0 0 6px 1px ${theme.accent}` } as any
        : { shadowColor: theme.accent, shadowOffset: { width: 0, height: 0 }, shadowOpacity: 0.5, shadowRadius: 6, elevation: 6 })
    : {};

  useEffect(() => {
    fetchRoom();
  }, [roomId]);

  const fetchRoom = async () => {
    try {
      const { data, error } = await supabase
        .from('rooms')
        .select('*')
        .eq('id', roomId)
        .single();

      if (error) throw error;

      setName(data.name);
      setDescription(data.description || '');
    } catch (error: any) {
      console.error('Error fetching room:', error);
      Alert.alert('Error', 'Failed to load room details');
      navigation.goBack();
    }
  };

  const handleEditRoom = async () => {
    if (!name.trim()) {
      setError('Room name is required');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const { error } = await supabase
        .from('rooms')
        .update({
          name: name.trim(),
          description: description.trim() || null,
          updated_with: getPlatform()
        })
        .eq('id', roomId)
        .eq('user_id', user?.id)
        .select();

      if (error) throw error;

      await refresh();
      navigation.dispatch(
        CommonActions.navigate({
          name: 'RoomsTab',
          params: { 
            refresh: true,
            ...(route.params.returnPage !== undefined && {
              page: route.params.returnPage
            })
          }
        })
      );
    } catch (error: any) {
      console.error('Error updating room:', error);
      setError('Failed to update room');
    } finally {
      setLoading(false);
    }
  };

  const shadowStyle = Platform.OS === 'web' 
    ? { boxShadow: '0 2px 8px 0 rgba(0,0,0,0.08)' } 
    : { 
        elevation: 3,
        boxShadow: '0 2px 8px 0 rgba(0,0,0,0.08)'
      };

  return (
    <SafeAreaView style={[styles.container, { backgroundColor: theme.background }]}>
      <StatusBar barStyle="light-content" />
      <KeyboardAvoidingView
        behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
        style={[styles.container, { backgroundColor: theme.background }]}
      >
        <ScrollView contentContainerStyle={styles.scrollContent} keyboardShouldPersistTaps="handled">
          {/* Modern Header */}
          <View style={{ paddingTop: 32, paddingBottom: 16, paddingHorizontal: 20, alignItems: 'center', backgroundColor: theme.background }}>
            <View style={{ position: 'absolute', left: 20, top: 32 }}>
              <TouchableOpacity
                style={{ padding: 8, borderRadius: 20, backgroundColor: theme.inputBackground }}
                onPress={() => navigation.goBack()}
                accessibilityLabel="Back"
                accessibilityRole="button"
              >
                <Ionicons name="arrow-back" size={26} color={theme.text} />
              </TouchableOpacity>
            </View>
            <Text style={{ color: theme.text, fontSize: 17, fontWeight: '500', textAlign: 'center', marginBottom: 4 }}>Edit Room</Text>
            <View style={{ height: 2, backgroundColor: theme.border, width: 60, borderRadius: 2, marginTop: 8 }} />
          </View>

          {/* Modern Card Form */}
          <View style={{ padding: 20, alignItems: 'center' }}>
            <View style={{ backgroundColor: theme.card, borderRadius: 16, padding: 24, width: '100%', maxWidth: 420, ...shadowStyle, ...cardGlow }}>
              {/* Error Message */}
              {error && (
                <View style={{ backgroundColor: '#ffeaea', borderRadius: 8, padding: 12, marginBottom: 16 }}>
                  <Text style={{ color: theme.error || '#d32f2f', fontWeight: '600', fontSize: 15 }}>{error}</Text>
                </View>
              )}

              {/* Room Name */}
              <FormLabel>Room Name *</FormLabel>
                <FormTextInput
                style={{
                  backgroundColor: theme.inputBackground,
                  color: theme.text,
                  borderColor: theme.border,
                  borderWidth: 1,
                  borderRadius: 8,
                  paddingVertical: 12,
                  paddingHorizontal: 14,
                  fontSize: 16,
                }}
                  value={name}
                  onChangeText={setName}
                  placeholder="Enter room name"
                placeholderTextColor={theme.secondaryText}
                  autoCapitalize="words"
                  maxLength={25}
                returnKeyType="next"
                />
                <Text style={{ color: theme.secondaryText, fontSize: 12, textAlign: 'right', marginTop: 2 }}>{name.length}/25 characters</Text>

              {/* Description */}
              <FormLabel>Description</FormLabel>
                <FormTextInput
                style={{
                  backgroundColor: theme.inputBackground,
                  color: theme.text,
                  borderColor: theme.border,
                  borderWidth: 1,
                  borderRadius: 8,
                  paddingVertical: 12,
                  paddingHorizontal: 14,
                  fontSize: 16,
                  minHeight: 80,
                  textAlignVertical: 'top',
                  marginBottom: 24,
                }}
                  value={description}
                  onChangeText={setDescription}
                  placeholder="Enter room description"
                placeholderTextColor={theme.secondaryText}
                  multiline
                  numberOfLines={4}
                  maxLength={200}
                />

              {/* Save Button */}
              <ThemedButton
                title={loading ? 'Updating...' : 'Save Changes'}
                onPress={handleEditRoom}
                disabled={loading}
                style={{ marginTop: 4 }}
                accessibilityLabel="Save Changes"
              />
            </View>
          </View>
        </ScrollView>
      </KeyboardAvoidingView>
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  scrollContent: {
    flexGrow: 1,
  },
  header: {
    marginBottom: 30,
    padding: 20,
    borderBottomWidth: 1,
    borderBottomColor: '#333',
  },
  headerContent: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    marginBottom: 0,
  },
  backButton: {
    padding: 8,
    justifyContent: 'center',
    alignItems: 'center',
    marginTop: 6,
  },
  title: {
    fontSize: 18,
    fontWeight: '600',
  },
  content: {
    padding: 20,
  },
  webContent: {
    maxWidth: 1200,
    marginHorizontal: 'auto',
    width: '100%',
  },
  form: {
    width: '100%',
  },
  webForm: {
    maxWidth: 1200,
    marginHorizontal: 'auto',
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 32,
    alignItems: 'flex-start',
  },
  formColumn: {
    width: '100%',
    marginBottom: 16,
  },
  webFormColumn: {
    flex: 1,
    minWidth: 300,
    maxWidth: 400,
  },
  label: {
    color: '#fff',
    fontSize: 16,
    marginBottom: 8,
    marginTop: 0,
  },
  input: {
    backgroundColor: '#2a2a2a',
    borderRadius: 8,
    padding: 12,
    color: '#fff',
    fontSize: 16,
    borderWidth: 1,
    borderColor: '#333',
    width: '100%',
    marginBottom: 16,
    height: 44,
  },
  textArea: {
    height: 100,
    textAlignVertical: 'top',
    paddingTop: 12,
  },
  button: {
    backgroundColor: '#007AFF',
    borderRadius: 8,
    padding: 16,
    alignItems: 'center',
    marginTop: 20,
    width: '100%',
    maxWidth: 400,
    height: 44,
    justifyContent: 'center',
  },
  buttonDisabled: {
    opacity: 0.5,
  },
  buttonText: {
    color: '#fff',
    fontSize: 14,
    fontWeight: '600',
  },
}); 